import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback } from '@theme/utilities';

/**
 * Hydrates the "Recently viewed" section.
 *
 * The list of viewed product ids only exists in localStorage, so the section is
 * rendered empty on the server and filled in here: we ask the Section Rendering
 * API for this same section against a `/search?q=id:1 OR id:2...` URL, which is
 * the same mechanism predictive search uses for its recently viewed products.
 *
 * If there is nothing to show (first-time visitor, or the only viewed product is
 * the one currently being looked at) nothing is injected and the section stays
 * empty, so it occupies no space at all.
 */
class RecentlyViewedProducts extends HTMLElement {
  /** @type {boolean} */
  #loaded = false;

  connectedCallback() {
    if (this.#loaded) return;
    this.#loaded = true;

    requestIdleCallback(() => this.#render());
  }

  /**
   * The recently viewed product ids, minus the product currently being viewed.
   * The current product is always written to the list on page load, so it is
   * always the first entry and must be filtered out.
   * @returns {string[]} The product ids to render.
   */
  get #productIds() {
    const currentProductId = String(this.dataset.productId ?? '');
    const limit = Number.parseInt(this.dataset.maxProducts ?? '4', 10) || 4;

    return RecentlyViewed.getProducts()
      .filter((/** @type {string} */ id) => String(id) !== currentProductId)
      .slice(0, limit);
  }

  async #render() {
    const { sectionId } = this.dataset;
    if (!sectionId) return;

    const productIds = this.#productIds;
    if (productIds.length === 0) return;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', productIds.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    let markup = null;

    try {
      markup = await sectionRenderer.getSectionHTML(sectionId, false, url);
    } catch (error) {
      return;
    }

    if (!markup) return;

    const incoming = new DOMParser()
      .parseFromString(markup, 'text/html')
      .querySelector('recently-viewed-products');

    // Search returned nothing renderable (deleted or unpublished products).
    if (!incoming || incoming.children.length === 0) return;

    this.innerHTML = incoming.innerHTML;
    this.dataset.loaded = 'true';
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
